/** @jsxImportSource @emotion/react */

import { FC } from "react";
import { css } from "@emotion/react";
import { TextField as MdTextField } from "@rmwc/textfield";
import "@rmwc/textfield/styles";
import useConfigs from "../../ConfigContext/use-configs";
import useTheme from "../../../commons/hooks/use-theme";
import Text from "../../DataDisplay/Text";
import { useHandleChange } from "./commons/textfield-hooks";

export type TextFieldType = {
  label?: string;
  placeholder?: string;
  dir?: string;
  outlined?: boolean;
  disabled?: boolean;
  defaultValue?: string | number;
  value?: string | number;
  onChange?: Function;
  name?: string;
  required?: boolean;
  type?: string;
  readOnly?: boolean;
  hint?: string;
  invalid?: boolean;
  errorMessage?: string;
  textarea?: boolean;
  rows?: number;
  success?: boolean;
  icon?: object;
  trailingIcon?: object;
  border?: boolean;
  isENNumber?: boolean;
  className?: string;
  maxLength?: number;
};

const TextField: FC<TextFieldType> = (props) => {
  const theme = useTheme();
  let configs = useConfigs();

  const {
    label = "",
    placeholder = "",
    dir = configs.direction,
    outlined = true,
    disabled = false,
    value = "",
    onChange,
    name,
    required = false,
    type,
    readOnly = false,
    hint = "",
    invalid = false,
    errorMessage = "",
    textarea = false,
    rows,
    success,
    icon,
    trailingIcon,
    border = false,
    isENNumber = false,
    className,
    maxLength,
  } = props;

  const { inputValue, handleChange } = useHandleChange(value, onChange, name, isENNumber);

  let successBorder;
  let textColor;
  let hasBorder;
  let heightSize;

  if (border) {
    hasBorder = 0;
  }
  if (textarea) {
    heightSize = "auto";
  } else {
    heightSize = "50px";
  }
  if (invalid) {
    successBorder = theme.material.error;
    textColor = theme.material.error;
  } else {
    if (success) {
      successBorder = theme.successColor;
      textColor = theme.successColor;
    } else if (readOnly || disabled) {
      successBorder = theme.material.textDisabledOnLight;
      textColor = theme.material.textDisabledOnLight;
    } else {
      successBorder = theme.material.onSurface;
      textColor = theme.material.onSurface;
    }
  }

  const styles = {
    wrapper: css`
      .mdc-text-field--disabled .mdc-text-field__input {
        cursor: not-allowed;
        pointer-events: auto;
      }
      .mdc-text-field--textarea.mdc-text-field--disabled {
        background: transparent !important;
      }
    `,
    input: css`
      width: 100% !important;
      height: ${heightSize};
      // overflow: visible;
      -webkit-text-fill-color: ${theme.material.onSurface};

      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
      }

      input,
      textarea,
      input::placeholder,
      textarea::placeholder {
        color: ${theme.material.onSurface} !important;
      }

      .mdc-floating-label--float-above,
      .mdc-floating-label {
        color: ${textColor} !important;
      }

      label {
        color: ${textColor} !important;
      }

      .mdc-text-field__input:-webkit-autofill {
        -webkit-box-shadow: 0 0 0 100px ${theme.material.background} inset !important;
      }

      .mdc-notched-outline__leading,
      .mdc-notched-outline__notch,
      .mdc-notched-outline__trailing,
      [dir="rtl"] .mdc-notched-outline__trailing,
      .mdc-notched-outline__trailing[dir="rtl"],
      [dir="rtl"] .mdc-notched-outline__leading,
      .mdc-notched-outline__leading[dir="rtl"] {
        border-color: ${successBorder} !important;
        border: ${hasBorder} !important;
      }
    `,
  };

  return (
    <div dir={dir} className={className} css={styles.wrapper}>
      <MdTextField
        textarea={textarea}
        rows={rows}
        dir={dir}
        label={label}
        outlined={outlined}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleChange}
        value={inputValue}
        required={required}
        css={styles.input}
        type={type}
        readOnly={readOnly}
        invalid={invalid}
        icon={icon}
        trailingIcon={trailingIcon}
        characterCount
        maxLength={maxLength}
        helpText={{
          persistent: true,
          validationMsg: invalid,
          children: invalid ? (
            <Text tag="span" type={"textHint"} error={invalid}>
              {errorMessage}
            </Text>
          ) : (
            <Text tag="span" type={"textHint"}>
              {hint}
            </Text>
          ),
        }}
      />
    </div>
  );
};

export default TextField;
